// api/growth.ts — growth admin API client. Mirrors api/research.ts: typed wrappers over client.ts.
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, API_BASE_URL } from "./client.ts";
import { useAuthStore } from "@/stores/authStore.ts";

export interface GrowthAccount {
  id: string; handle: string; platform: string;
  characterRegistryId: string | null; bioCta: string | null;
  isOwn: boolean; health: "healthy" | "warming" | "shadowbanned"; growthEnabled: boolean;
  /** Make a first frame for every new idea automatically (cheapest model). */
  autoFirstFrame?: boolean;
  createdAt: string;
}
export interface AccountNiche { niche: string; mode: "blend" | "pivot"; audience: unknown; active: boolean; }
export interface ArcVideo {
  seq: number; role: "ramp" | "hit"; mode: "video" | "carousel" | "image";
  script?: string | null;            // mode=video — DERIVED from scriptParts (Phase 3)
  slides?: string[] | null;          // mode=carousel — DERIVED from slideItems (their on-frame text)
  caption?: string | null;           // mode=image — DERIVED from scene.captionBrief
  finalUrl?: string | null;          // Phase 4b: the finished video (Finish & Post)
  postUrl?: string | null;           // Phase 4b: what to post — finalUrl, else the production render
  // Phase 3 — the STRUCTURED item: what growth orders carry and what edits change.
  scriptParts?: ScriptParts | null;  // mode=video
  slideItems?: SlideItem[] | null;   // mode=carousel
  scene?: ImageScene | null;         // mode=image
  premiseSlice?: string | null;
  carriedState?: unknown; characterRef?: string | null;
  orderId?: string | null;
  assetUrl?: string | null;          // the primary/first url — always set alongside assetUrls
  assetUrls?: string[] | null;       // the full ordered set: a carousel's N slides, or one for video/image
  orderStatus?: string | null;       // the callback's last terminal status (succeeded|failed|cancelled), if any
  postedAt?: string | null;
  // The ENGINE's post caption (still/carousel copy step), stored from the completion callback.
  // Null for video and until the run completes. `caption` above is the director's brief.
  postCaption?: { hook: string | null; body: string | null; cta: string | null; hashtags: string[] | null } | null;
  /** Pilot first (0027): this part's own cheap first frame — a later part is seen before its video is made. */
  firstFrame?: { url: string | null; status: "none" | "making" | "ready" | "failed" | string };
}
/** hook / cta are null when the video's format has no hook / cta beat. */
export interface ScriptParts { hook: string | null; bodyParagraphs: string[]; cta: string | null }
export interface SlideItem { onFrameText: string; visualHint: string }
export interface ImageScene { sceneBrief: string; onFrameText: string | null; captionBrief: string }
/** A pre-production item edit — the structured item (a flat script/caption edit is refused by the backend). */
export type ArcVideoEdit = { characterRef?: string | null; scriptParts?: ScriptParts; slideItems?: SlideItem[]; scene?: ImageScene };

export interface GrowthArc {
  id: string; accountId: string; niche: string; patternId: string;
  premise: string | null; payoff: string | null; cadenceDays: unknown; source: string;
  status: string; createdAt: string; videos: ArcVideo[];
  /** The idea's first frame (backend 0025): made with the cheapest image model; status none|making|ready|failed. */
  firstFrame?: { url: string | null; status: string };
}
export interface CampaignResult {
  generated: number;
  campaign: Array<{ arcId: string; results: Array<{ seq: number; ok: boolean; runId?: string; error?: string }> }>;
  arcs: GrowthArc[];
}

// Phase 3 — the review surface's per-arc detail shape (matches arcReviewDetail).
export interface GrowthArcReviewVideo {
  seq: number; role: string; mode: string;
  script: string | null; caption: string | null; characterRef: string | null;
  runId: string | null; orderStatus: string | null; assetUrl: string | null; postedAt: string | null;
  /** Phase 4b: the finished video (Finish & Post), and the URL to post — the finished one when there is one. */
  finalUrl?: string | null; postUrl?: string | null;
}
export interface GrowthArcReview {
  arcId: string; status: string; niche: string; patternId: string; premise: string;
  videos: GrowthArcReviewVideo[];
}

/** Session 6 W3: a growth account's publishing channel (autopilot posting). */
export interface GrowthChannel { id: string; channel: string; status: string; status_reason: string | null; tenant_ref: string; account_name: string }
export interface GrowthAccountChannel {
  account: { id: string; handle: string; platform: string; social_channel_id: string | null };
  linked: GrowthChannel | null;
  /** why autopilot would not post for this account (null = it can) */
  stalled: string | null;
  /** the channels connected FOR GROWTH — the only ones it can be linked to */
  choices: GrowthChannel[];
}

export const growthApi = {
  // Session 6 W3: the account's channel for autopilot posting
  getAccountChannel: (id: string) => apiGet<GrowthAccountChannel>(`/admin/growth/accounts/${id}/channel`),
  linkAccountChannel: (id: string, socialChannelId: string | null) =>
    apiPost<{ ok: boolean; socialChannelId: string | null }>(`/admin/growth/accounts/${id}/channel`, { socialChannelId }),
  connectForGrowth: (id: string, channel: "tiktok" | "youtube", returnUrl: string) =>
    apiPost<{ authorizeUrl: string; state: string }>(`/admin/growth/accounts/${id}/connect`, { channel, returnUrl }),
  listAccounts: () => apiGet<{ accounts: GrowthAccount[] }>("/admin/growth/accounts"),
  getAccount: (id: string) => apiGet<{ account: GrowthAccount }>(`/admin/growth/accounts/${id}`),
  createAccount: (body: { handle: string; platform?: string; characterRegistryId?: string | null; bioCta?: string | null }) =>
    apiPost<{ id: string; account: GrowthAccount }>("/admin/growth/accounts", body),
  deleteAccount: (id: string) =>
    apiDelete<{ deleted: boolean; id: string }>(`/admin/growth/accounts/${id}`),
  updateAccount: (id: string, body: Partial<Pick<GrowthAccount, "characterRegistryId" | "bioCta" | "health" | "growthEnabled">>) =>
    apiPatch<{ account: GrowthAccount }>(`/admin/growth/accounts/${id}`, body),

  listNiches: (id: string) => apiGet<{ niches: AccountNiche[] }>(`/admin/growth/accounts/${id}/niches`),
  addNiche: (id: string, body: { niche: string; mode: "blend" | "pivot" }) =>
    apiPost<{ niches: AccountNiche[] }>(`/admin/growth/accounts/${id}/niches`, body),
  removeNiche: (id: string, niche: string) =>
    apiDelete<{ niches: AccountNiche[] }>(`/admin/growth/accounts/${id}/niches/${encodeURIComponent(niche)}`),

  listArcs: (id: string) => apiGet<{ arcs: GrowthArc[] }>(`/admin/growth/accounts/${id}/arcs`),
  // Phase 3.1 — one arc's videos for the review surface.
  arcDetail: (arcId: string) => apiGet<GrowthArcReview>(`/admin/growth/arcs/${arcId}/detail`),
  // Phase 5.1 — edit arc-level fields / transition status (authored↔approved) before produce.
  editArc: (arcId: string, body: { premise?: string; payoff?: string; status?: "authored" | "approved" }) =>
    apiPatch<{ arcId: string; ok: boolean; applied: unknown }>(`/admin/growth/arcs/${arcId}`, body),
  // Phase 5.2 — edit one video's pre-production fields (before any order).
  editArcVideo: (arcId: string, seq: number, body: ArcVideoEdit) =>
    apiPatch<{ arcId: string; seq: number; ok: boolean; applied: unknown }>(`/admin/growth/arcs/${arcId}/videos/${seq}`, body),
  generateArcs: (id: string, body: { niche?: string; count?: number; ageBand?: string; gender?: string; category?: string; speaks?: boolean; patternId?: string; format?: "video" | "carousel" | "image" }) =>
    apiPost<{ generated: number; arcIds: string[]; arcs: GrowthArc[] }>(`/admin/growth/accounts/${id}/arcs/generate`, body),
  /** One idea's first frame, on demand (cheapest image model). */
  makeFirstFrame: (arcId: string) => apiPost<{ status: string }>(`/admin/growth/arcs/${arcId}/first-frame`, {}),
  /** The account's "first frame for every new idea" toggle. */
  setAccountSettings: (accountId: string, body: { autoFirstFrame: boolean }) => apiPut<{ autoFirstFrame: boolean }>(`/admin/growth/accounts/${accountId}/settings`, body),
  /** `seqs`: only these parts (pilot first: "Make it" → [first]; Continue → the rest). Omitted = every part. */
  produceArc: (arcId: string, body?: { providedAssetIds?: string[]; seqs?: number[] }) =>
    apiPost<{ arcId: string; results: Array<{ seq: number; ok: boolean; runId?: string; error?: string }> }>(`/admin/growth/arcs/${arcId}/produce`, body ?? {}),
  /** Pilot first: a cheap first frame for each of these later parts (background), without ordering their videos. */
  partFrames: (arcId: string, seqs: number[]) => apiPost<{ queued: number[] }>(`/admin/growth/arcs/${arcId}/part-frames`, { seqs }),
  /** (creative-1) `url`: the post's link — its views, follows and shares are then read at +0, +24, the judging window and +72 h. */
  markPosted: (arcId: string, seq: number, postedAt?: string, url?: string) =>
    apiPost<{ ok: boolean; post?: { status: "recorded" | "duplicate"; permalink: string } | { status: "not_measured"; reason: string } }>(`/admin/growth/arcs/${arcId}/posted`, { seq, postedAt, ...(url ? { url } : {}) }),

  // Phase 4 — one call: niche (+ optional provided image set) → author a mixed-format batch → produce all of it.
  campaign: (accountId: string, body?: { niche?: string; count?: number; providedAssetIds?: string[]; speaks?: boolean; patternId?: string; format?: "video" | "carousel" | "image" }) =>
    apiPost<CampaignResult>(`/admin/growth/accounts/${accountId}/campaign`, body ?? {}),

  // Phase 3 — multipart, so it bypasses client.ts's JSON-only apiRequest (see that file's header:
  // it unconditionally JSON.stringifies the body). Hand-rolled the same way apiRequest attaches auth.
  uploadImages: async (files: File[]): Promise<{ assetIds: string[] }> => {
    const form = new FormData();
    for (const f of files) form.append("images", f, f.name);
    const token = useAuthStore.getState().token;
    const res = await fetch(`${API_BASE_URL}/admin/growth/upload-images`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
      credentials: "omit",
    });
    if (!res.ok) {
      // growth's routes send { error: "message" } (a flat string), not client.ts's { error: { code,
      // message } } shape — matches every other error response already in routes/growth/arcs.ts.
      const body = await res.json().catch(() => null) as { error?: string } | null;  // catch-reason: an error body that isn't JSON: the status is named instead (below)
      throw new Error(body?.error ?? `image upload failed (${res.status})`);
    }
    return res.json();
  },

  measureArc: (arcId: string) =>
    apiPost<{ score: ArcScore }>(`/admin/growth/arcs/${arcId}/measure`),
  // One arc's own score (tf-041 Part B). Returns null when not yet measured (the route
  // 404s in that case) — the caller renders "not measured" vs a real zero score.
  // (tf3-31, A17) ONLY a 404 is "not measured"; any other failure throws, and the caller shows it.
  getArcScore: async (arcId: string): Promise<ArcScore | null> => {
    try {
      const r = await apiGet<{ score: ArcScore }>(`/admin/growth/arcs/${arcId}/score`);
      return r.score;
    } catch (e) {   // catch-reason: a 404 is "not measured yet"; anything else is rethrown
      if ((e as { status?: number }).status === 404) return null;
      throw e;
    }
  },
  leaderboard: (niche?: string) =>
    apiGet<{ leaderboard: LeaderboardRow[] }>(`/admin/growth/leaderboard${niche ? `?niche=${encodeURIComponent(niche)}` : ""}`),
  reuseArc: (arcId: string, accountIds: string[]) =>
    apiPost<{ sourceArcId: string; created: Array<{ accountId: string; arcId?: string; error?: string }> }>(`/admin/growth/arcs/${arcId}/reuse`, { accountIds }),
};

// ── Review console (B1) ───────────────────────────────────────────────────────

/** A segment on the storyboard strip (ingest GET /orders/:runId/storyboard → beats[]). */
export interface StoryboardBeat {
  beatId: string;
  position: number | null;
  imageUrl: string | null;
  cardUrl?: string | null;   // the frame with its on-screen text composited — null when the beat has none
  prompt: string | null;
  subjectText: string | null;   // frameAst.slots.subject.text — the editable "what the frame depicts"
  onScreenText: unknown;
  regenerationsUsed: number;
  regenerationsMax: number;
}
export interface StoryboardView {
  runId: string;
  status: string;          // the run status; 409 (not at a reviewable gate) is surfaced as an error
  beats: StoryboardBeat[];
}

/** One segment's full render inputs + production artefacts (merged CMS read).
 *  `inputs` is intentionally open — every render input (script, frameAst, motionAst, audioIntent,
 *  onScreenText, castBinding, duration, transitions, pantryKey, …) rides here as the CMS returns it,
 *  so new/deferred axes (frame/motion redirect later) need no shape change. */
export interface SegmentDetail {
  beatId: string;
  inputs: Record<string, unknown>;
  production: Record<string, unknown> | null;
}

// The lifecycle roll-up lives in its own dependency-free module (api/videoState.ts) so it is
// unit-testable without the browser deps this file imports. Re-exported here for the page.
export { videoState, videoStateBadge, type VideoState } from "./videoState.ts";

export const growthReviewApi = {
  storyboard: (arcId: string, seq: number) =>
    apiGet<StoryboardView>(`/admin/growth/arcs/${arcId}/videos/${seq}/storyboard`),
  segment: (arcId: string, seq: number, beatId: string, cmsId: string) =>
    apiGet<SegmentDetail>(`/admin/growth/arcs/${arcId}/videos/${seq}/segments/${beatId}?cmsId=${encodeURIComponent(cmsId)}`),
  editText: (arcId: string, seq: number, beatId: string, onScreenText: string) =>
    apiPost<unknown>(`/admin/growth/arcs/${arcId}/videos/${seq}/segments/${beatId}/text`, { onScreenText }),
  // subjectText (optional) edits what the frame depicts before the re-render; omitted = re-roll as-is.
  regenerate: (arcId: string, seq: number, beatId: string, subjectText?: string) =>
    apiPost<{ status: string; beatId: string; regenerationsUsed: number; regenerationsMax: number }>(
      `/admin/growth/arcs/${arcId}/videos/${seq}/segments/${beatId}/regenerate`, subjectText ? { subjectText } : {}),
  approve: (arcId: string, seq: number, note?: string) =>
    apiPost<unknown>(`/admin/growth/arcs/${arcId}/videos/${seq}/approve`, note ? { note } : {}),
  // Ends the video's live run (ingest refuses a finished one with 409) and closes any open gate.
  cancel: (arcId: string, seq: number, reason?: string) =>
    apiPost<{ status: string; runId: string }>(`/admin/growth/arcs/${arcId}/videos/${seq}/cancel`, reason ? { reason } : {}),
};

// ── Phase 0/0.5/1/3 — the arc review pipelines (spread/pick/status) + operator console queue ─────

/** OrderStatusResponse proxied from the engine (GET /orders/:runId), via the growth status route. */
export interface RunStatus {
  runId?: string;
  status?: string;                                   // pending | running | succeeded | failed | ...
  progress?: { phase?: string; pct?: number; done?: number; total?: number } | null;
  gate?: { gateName: string; policy: string; blockedBy?: string[] } | null;
  failure?: { category: string | null; reason: string | null } | null;
  refusal?: { code: string; reason: string; remedies?: string[] } | null;
  renderUrl?: string | null;
  [k: string]: unknown;                              // pass-through for any other engine fields
}

/** One still/carousel spread candidate (ingest GET .../{kind}-spread → candidates[]). */
export interface SpreadCandidate {
  candidateId: string;
  presetId?: string;
  axisValues?: Record<string, unknown> | null;
  previewUrl?: string | null;                        // still
  slideCount?: number;                               // carousel
  previewUrls?: string[] | null;                     // carousel
}
export interface SpreadView {
  runId: string;
  kind: "still" | "carousel";
  candidates: SpreadCandidate[];
}

/** One row in the operator work queue (GET /admin/growth/queue). */
export interface QueueItem {
  arcId: string;
  runId: string;
  /** Operator visuals G7: what it looks like — the rendered video, the still, or the carousel's first slide. */
  picture?: { kind: "video" | "image"; url: string } | null;
  kind: "video" | "carousel" | "still";
  reason: string;
  bucket: "awaiting_review" | "rendering" | "failed" | "done";
  phase: string | null;
  pct: number | null;
  gate: { gateName: string; policy: string; blockedBy?: string[] } | null;
  spreadCount: number;
  chosenCandidateId: string | null;
  createdAt: string;
  updatedAt: string;
  reachable: boolean;
  takingTooLong: boolean;
  failure?: { category: string | null; reason: string | null } | null;
  recoveryAction?: "retry" | "edit_regenerate" | "abandon";
}
export interface QueueView {
  awaitingMe: number;
  counts: Partial<Record<QueueItem["bucket"], number>>;
  total: number;
  items: QueueItem[];
}

export const growthQueueApi = {
  /** The operator's home screen: everything in flight, actionable-first, with the awaiting-me badge. */
  queue: (opts?: { accountId?: string; bucket?: string; limit?: number }, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (opts?.accountId) qs.set("accountId", opts.accountId);
    if (opts?.bucket) qs.set("bucket", opts.bucket);
    if (opts?.limit) qs.set("limit", String(opts.limit));
    const q = qs.toString();
    return apiGet<QueueView>(`/admin/growth/queue${q ? `?${q}` : ""}`, signal);
  },
  /** Proxy the engine's GET /orders/:runId for one run (phase/gate/failure). Polled by the review UI. */
  runStatus: (arcId: string, runId: string, signal?: AbortSignal) =>
    apiGet<RunStatus>(`/admin/growth/arcs/${arcId}/runs/${runId}/status`, signal),
};

// ── Phase 5.1 — push transport: subscribeToCandidates (SSE with polling fallback) ───────────────
// The SPEC's `subscribeToCandidates` abstraction. Callers get one function: "tell me when this run
// changes." It opens an EventSource to the growth SSE route; on each pushed nudge (or on the polling
// fallback tick) it invokes onChange, which the UI uses to re-read authoritative status. Push and poll
// agree because the SSE event is only a NUDGE — the UI still reads GET .../status for the truth.
// EventSource can't set Authorization headers, so the token rides as ?token= (the backend auth
// middleware's SSE fallback accepts it for /events routes). Returns an unsubscribe fn.
export function subscribeToRun(
  arcId: string,
  runId: string,
  onChange: () => void,
  opts?: { pollMs?: number },
): () => void {
  const pollMs = opts?.pollMs ?? 5000;
  let es: EventSource | null = null;
  let poll: ReturnType<typeof setInterval> | null = null;
  let closed = false;

  // Polling fallback always runs at a slow cadence so a dropped/blocked SSE still updates. When SSE
  // is healthy this is just a low-frequency safety net; when it fails it IS the transport.
  poll = setInterval(() => { if (!closed) onChange(); }, pollMs);

  try {
    const token = useAuthStore.getState().token;
    const url = `${API_BASE_URL}/admin/growth/arcs/${arcId}/runs/${runId}/events${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    es = new EventSource(url);
    es.onmessage = () => { if (!closed) onChange(); };
    es.onerror = () => { /* keep the poll fallback; EventSource auto-reconnects */ };
  } catch {
    // EventSource unavailable (SSR / old env) — the poll fallback covers it.
  }

  return () => {
    closed = true;
    if (poll) clearInterval(poll);
    if (es) es.close();
  };
}

// ── Phase 5.0 — cost signal: a per-session render tally ─────────────────────────────────────────
// Cheap pipelines invite blind spend (GAP O8). We already show a per-generate render count on the
// spread page; this adds a running SESSION tally the operator can see accumulate. In-memory only
// (per tab) — a soft awareness nudge, not billing. Reset on reload by design.
let _sessionRenderCount = 0;
export const sessionCost = {
  add(n: number) { _sessionRenderCount += Math.max(0, n); return _sessionRenderCount; },
  get() { return _sessionRenderCount; },
  reset() { _sessionRenderCount = 0; },
};

export const growthSpreadApi = {
  // Image (still) — generate N candidates, read the spread, pick one.
  generateStill: (arcId: string, spreadCount: number) =>
    apiPost<{ arcId: string; runId: string; spreadCount: number }>(`/admin/growth/arcs/${arcId}/still-spread`, { spreadCount }),
  readStill: (arcId: string, signal?: AbortSignal) =>
    apiGet<SpreadView>(`/admin/growth/arcs/${arcId}/still-spread`, signal),
  pickStill: (arcId: string, candidateId: string) =>
    apiPost<{ status: string; runId: string; picked: string }>(`/admin/growth/arcs/${arcId}/still-pick`, { candidateId }),

  // Carousel — same shape, kind=carousel; per-slide review is the storyboard routes (growthReviewApi).
  generateCarousel: (arcId: string, spreadCount: number) =>
    apiPost<{ arcId: string; runId: string; spreadCount: number }>(`/admin/growth/arcs/${arcId}/carousel-spread`, { spreadCount }),
  readCarousel: (arcId: string, signal?: AbortSignal) =>
    apiGet<SpreadView>(`/admin/growth/arcs/${arcId}/carousel-spread`, signal),
  pickCarousel: (arcId: string, candidateId: string) =>
    apiPost<{ status: string; runId: string; picked: string }>(`/admin/growth/arcs/${arcId}/carousel-pick`, { candidateId }),
};

// Phase 4 — the arc's CONCEPT is its first item's REAL run, held at review: a video at the storyboard gate
// (beat 0's captioned card), a carousel/still at the spread gate (candidates to pick from). Approving
// advances that run. Replaces the keyframe-only concept frames (an image no run ever used).
export type ConceptKind = "video" | "carousel" | "still";
export interface ConceptBeat {
  beatId: string; imageUrl: string | null; cardUrl: string | null; subjectText: string | null;
  regenerationsUsed: number; regenerationsMax: number;
}
export interface ConceptCandidate { candidateId: string; previewUrl?: string | null; previewUrls?: string[] | null }
export type ConceptState =
  | { state: "none" }
  | { state: "pending"; runId: string; kind: ConceptKind; progress: unknown }
  | { state: "ready"; runId: string; kind: "video"; beat: ConceptBeat }
  | { state: "ready"; runId: string; kind: "carousel" | "still"; candidates: ConceptCandidate[] }
  | { state: "approved"; runId: string; kind: ConceptKind }
  | { state: "failed" | "cancelled"; runId: string; kind: ConceptKind; reason: string | null };
export const growthConceptApi = {
  /** Place the first item's order (spends — image cost now, clips on approve). Returns an existing run. */
  make: (arcId: string) =>
    apiPost<{ arcId: string; runId: string; placed: boolean }>(`/admin/growth/arcs/${arcId}/concept`, {}),
  read: (arcId: string, signal?: AbortSignal) =>
    apiGet<{ arcId: string; seq: number; concept: ConceptState }>(`/admin/growth/arcs/${arcId}/concept`, signal),
  /** video: approve the storyboard; carousel/still: pick a candidate (candidateId required). */
  approve: (arcId: string, candidateId?: string) =>
    apiPost<{ arcId: string; runId: string; approved: boolean }>(`/admin/growth/arcs/${arcId}/concept/approve`, candidateId ? { candidateId } : {}),
  /** video: re-make one beat's frame (optional subject edit); carousel/still: supersede with a new run. */
  regenerate: (arcId: string, body: { beatId?: string; subjectText?: string } = {}) =>
    apiPost<unknown>(`/admin/growth/arcs/${arcId}/concept/regenerate`, body),
  reject: (arcId: string) =>
    apiPost<{ arcId: string; cancelledRunId: string }>(`/admin/growth/arcs/${arcId}/concept/reject`, {}),
};

// Phase 5.3 — attach an approved/done artifact to its account for posting (the deferred publish seam).
export interface AttachResult {
  arcId: string; seq: number; runId: string; attachedAt: string;
  finalUrl: string | null;
  delivery: { wired: boolean; provider: string; note: string };
}
export const growthPublishApi = {
  attach: (arcId: string, seq: number, note?: string) =>
    apiPost<AttachResult>(`/admin/growth/arcs/${arcId}/attach`, note ? { seq, note } : { seq }),
};

export interface ArcScore {
  arcId: string; accountId: string; niche: string | null;
  followersGained: number | null; windowFrom: string | null; windowTo: string | null; score: number | null;
}
export interface LeaderboardRow {
  arcId: string; accountId: string; niche: string | null;
  followersGained: number | null; score: number | null; patternId: string | null;
  premise: string | null; status: string | null;
}
